import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Button,
  Form,
  InputGroup,
  Badge,
  Spinner,
  Alert,
  Modal,
  Table,
  Row,
  Col,
  Pagination,
} from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import { shouldShowSupportChatSubject } from './supportChatSubject';
import SupportMessageComposer from './SupportMessageComposer';
import SupportMessageList from './SupportMessageList';

const SupportChat = ({
  chatId,
  onClose,
  isAdmin = false,
  embeddedInChatApp = false,
  onMessagesRead,
  onUnreadTotalsRefresh,
  showBackButton = false,
  /** Called when GET /support/chats/:id/ returns 404 (stale id after data loss); parent should reload list / reselect. */
  onChatMissing,
  /**
   * Non-admin: row from GET /support/chats/ (detail shape, includes messages). When set for this chatId,
   * initial load skips a duplicate GET /support/chats/<id>/.
   */
  prefetchedChatDetail = null,
}) => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const prevMessagesMetaRef = useRef({ count: 0, lastId: null });
  const fileInputRef = useRef(null);
  const messageRefs = useRef(new Map());
  const highlightTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const disconnectGraceTimeoutRef = useRef(null);
  const onMessagesReadRef = useRef(onMessagesRead);
  const messagesReadScheduleRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const isMountedRef = useRef(true);
  const consumedListPayloadRef = useRef(false);
  const prefetchedDetailRef = useRef(prefetchedChatDetail);
  prefetchedDetailRef.current = prefetchedChatDetail;

  const [translatingMessageId, setTranslatingMessageId] = useState(null);
  const [markingUnreadId, setMarkingUnreadId] = useState(null);
  const [composerInsert, setComposerInsert] = useState(null);

  // Reply to message (admin only)
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  /** Admin: block under header (email, stores, …) — toggle to free vertical space while chatting. */
  const [adminUserInfoVisible, setAdminUserInfoVisible] = useState(true);
  /** Admin: AI reply draft (question pasted → suggest → insert into composer; never auto-sends). */
  const [aiQuestionDraft, setAiQuestionDraft] = useState('');
  const [aiSuggestedReply, setAiSuggestedReply] = useState('');
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [aiSuggestError, setAiSuggestError] = useState('');

  // Orders modal state
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersError, setOrdersError] = useState('');
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(0);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersSearch, setOrdersSearch] = useState('');
  const [ordersStatusFilter, setOrdersStatusFilter] = useState('');
  const [statusChoices, setStatusChoices] = useState([]);
  const [expandedOrderRow, setExpandedOrderRow] = useState(null);
  
  // Keep the ref updated
  useEffect(() => {
    onMessagesReadRef.current = onMessagesRead;
  }, [onMessagesRead]);

  const scheduleMessagesRead = useCallback(() => {
    clearTimeout(messagesReadScheduleRef.current);
    messagesReadScheduleRef.current = setTimeout(() => {
      onMessagesReadRef.current?.();
    }, 400);
  }, []);

  useEffect(() => () => {
    clearTimeout(messagesReadScheduleRef.current);
  }, []);

  useEffect(() => {
    setAdminUserInfoVisible(true);
    shouldAutoScrollRef.current = true;
    prevMessagesMetaRef.current = { count: 0, lastId: null };
  }, [chatId]);

  useEffect(() => {
    setAiQuestionDraft('');
    setAiSuggestedReply('');
    setAiSuggestError('');
    setAiSuggestLoading(false);
  }, [chatId]);

  // Get WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const token = api.getToken();
    if (!token) return null;
    return `${protocol}//${host}/ws/support/chat/${chatId}/?token=${encodeURIComponent(token)}`;
  }, [chatId]);

  // Connect to WebSocket
  useEffect(() => {
    isMountedRef.current = true;
    let initTimeout = null;
    
    if (!chatId) return;
    
    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      return;
    }

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (disconnectGraceTimeoutRef.current) {
      clearTimeout(disconnectGraceTimeoutRef.current);
      disconnectGraceTimeoutRef.current = null;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close(1000, 'Reconnecting');
      wsRef.current = null;
    }

    const createWebSocket = (url) => {
      if (!isMountedRef.current) return;
      
      // Prevent creating duplicate WebSocket for same chat
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        return;
      }

      let websocket;
      try {
        websocket = new WebSocket(url);
      } catch (err) {
        console.error('Failed to create WebSocket:', err);
        return;
      }

      websocket.onopen = () => {
        if (!isMountedRef.current) {
          websocket.close(1000, 'Component unmounted');
          return;
        }
        console.log('WebSocket connected successfully');
        if (disconnectGraceTimeoutRef.current) {
          clearTimeout(disconnectGraceTimeoutRef.current);
          disconnectGraceTimeoutRef.current = null;
        }
        setConnected(true);
        setError('');
        reconnectAttemptRef.current = 0;
        if (onMessagesReadRef.current) {
          scheduleMessagesRead();
        }
      };

      websocket.onmessage = (event) => {
        if (!isMountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'chat_history') {
            setMessages(data.data || []);
            scheduleMessagesRead();
          } else if (data.type === 'chat_message') {
            setMessages(prev => {
              const exists = prev.some(m => m.id === data.data.id);
              if (exists) {
                return prev.map(m => m.id === data.data.id ? { ...m, ...data.data } : m);
              }
              const newMessages = [...prev, data.data];
              if (data.data.sender_id !== user?.id) {
                scheduleMessagesRead();
              }
              return newMessages;
            });
          } else if (data.type === 'error') {
            setError(data.message);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      websocket.onerror = (err) => {
        console.error('WebSocket error:', err);
      };

      websocket.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);

        if (!isMountedRef.current) return;

        // Нормальное закрытие (смена чата / unmount) — сразу offline.
        if (event.code === 1000 || event.code === 1001) {
          setConnected(false);
          return;
        }

        // Краткий reconnect: не гасим connected сразу (иначе disabled input → потеря фокуса).
        if (disconnectGraceTimeoutRef.current) {
          clearTimeout(disconnectGraceTimeoutRef.current);
        }
        disconnectGraceTimeoutRef.current = setTimeout(() => {
          disconnectGraceTimeoutRef.current = null;
          if (!isMountedRef.current) return;
          const ws = wsRef.current;
          if (!ws || ws.readyState !== WebSocket.OPEN) {
            setConnected(false);
          }
        }, 2500);

        const delay = Math.min(3000 * Math.pow(2, reconnectAttemptRef.current), 30000);
        console.log(`WebSocket closed unexpectedly, reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current + 1})`);
        reconnectAttemptRef.current += 1;

        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            const newUrl = getWebSocketUrl();
            if (newUrl) createWebSocket(newUrl);
          }
        }, delay);
      };

      wsRef.current = websocket;
    };

    const initWebSocket = () => {
      if (!isMountedRef.current) return;
      // Use requestAnimationFrame to ensure DOM is ready (fixes Firefox)
      requestAnimationFrame(() => {
        if (isMountedRef.current) {
          createWebSocket(wsUrl);
        }
      });
    };

    // Wait for page to fully load before creating WebSocket (fixes Firefox issue)
    if (document.readyState === 'complete') {
      initWebSocket();
    } else {
      const onLoad = () => {
        window.removeEventListener('load', onLoad);
        initWebSocket();
      };
      window.addEventListener('load', onLoad);
    }

    return () => {
      isMountedRef.current = false;
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (disconnectGraceTimeoutRef.current) {
        clearTimeout(disconnectGraceTimeoutRef.current);
        disconnectGraceTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, getWebSocketUrl]);

  useEffect(() => {
    consumedListPayloadRef.current = false;
  }, [chatId]);

  // Load chat: non-admin list API includes messages; defer one frame so parent `chats` ref is current (avoids duplicate GET).
  useEffect(() => {
    if (!chatId) return;

    const tryApplyPrefetch = () => {
      const p = prefetchedDetailRef.current;
      if (
        !isAdmin &&
        !consumedListPayloadRef.current &&
        p &&
        p.id === chatId &&
        Array.isArray(p.messages)
      ) {
        consumedListPayloadRef.current = true;
        setChat(p);
        setMessages(p.messages);
        setLoading(false);
        return true;
      }
      return false;
    };

    if (tryApplyPrefetch()) {
      return undefined;
    }

    const raf = requestAnimationFrame(() => {
      if (!isMountedRef.current) return;
      if (tryApplyPrefetch()) return;
      loadChat();
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, isAdmin]);

  const isNearBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
  }, []);

  const scrollToBottom = useCallback((smooth = false) => {
    const el = messagesContainerRef.current;
    if (!el) return;
    if (smooth) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const handleMessagesScroll = useCallback(() => {
    shouldAutoScrollRef.current = isNearBottom();
  }, [isNearBottom]);

  // Scroll to bottom only when appropriate (not while user reads older messages).
  useEffect(() => {
    const prev = prevMessagesMetaRef.current;
    const count = messages.length;
    const lastMsg = count > 0 ? messages[count - 1] : null;
    const lastId = lastMsg?.id ?? null;

    const isFirstLoad = prev.count === 0 && count > 0;
    const appended = count > prev.count && lastId !== prev.lastId;
    const ownMessage = appended && lastMsg?.sender_id === user?.id;

    prevMessagesMetaRef.current = { count, lastId };

    const nearBottom = isNearBottom();
    if (isFirstLoad || ownMessage || (appended && nearBottom) || (shouldAutoScrollRef.current && nearBottom)) {
      requestAnimationFrame(() => {
        scrollToBottom(isFirstLoad || appended);
      });
      if (isFirstLoad || ownMessage) {
        shouldAutoScrollRef.current = true;
      }
    }
  }, [messages, user?.id, isNearBottom, scrollToBottom]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const setMessageRef = useCallback((messageId, node) => {
    if (!messageId) return;
    if (node) {
      messageRefs.current.set(messageId, node);
    } else {
      messageRefs.current.delete(messageId);
    }
  }, []);

  const jumpToMessage = useCallback((messageId) => {
    if (!messageId) return;
    const targetNode = messageRefs.current.get(messageId);
    if (!targetNode) {
      setError('Исходное сообщение не найдено в текущем списке.');
      return;
    }
    targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedMessageId(messageId);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedMessageId(null);
    }, 1800);
  }, []);

  const handleReplyToMessage = useCallback((message) => {
    if (!message) return;
    setReplyToMessage({
      id: message.id,
      content: (message.content || '').slice(0, 150),
      sender_name: message.sender_name || message.sender_email || t('support.support'),
    });
  }, [t]);

  // Send message (text owned by SupportMessageComposer — avoids list re-render on keystroke)
  const handleComposerSend = async ({ text, translatedText, showTranslationPreview }) => {
    if ((!text.trim() && !selectedFile) || !connected) return false;

    shouldAutoScrollRef.current = true;

    if (selectedFile) {
      try {
        setUploadingFile(true);
        const translatedContent = (isAdmin && translatedText && showTranslationPreview) ? translatedText : undefined;
        const response = await api.uploadSupportMessageAttachment(
          chatId,
          selectedFile,
          text.trim() || undefined,
          translatedContent,
          replyToMessage?.id || null
        );

        if (response.data) {
          setMessages((prev) => {
            const exists = prev.some((m) => m.id === response.data.id);
            if (exists) return prev;
            return [...prev, response.data];
          });
          setSelectedFile(null);
          setReplyToMessage(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
        return true;
      } catch (err) {
        setError(err.data?.errorText || err.message || t('support.failedToUploadFile'));
        return false;
      } finally {
        setUploadingFile(false);
      }
    }

    if (isAdmin && translatedText && showTranslationPreview) {
      try {
        await api.sendSupportMessage(chatId, text.trim(), translatedText, replyToMessage?.id);
        setReplyToMessage(null);
        await loadChat();
        return true;
      } catch (err) {
        setError(err.data?.errorText || err.message || 'Ошибка отправки сообщения');
        return false;
      }
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const payload = { type: 'chat_message', content: text.trim() };
      if (replyToMessage?.id) payload.reply_to_id = replyToMessage.id;
      wsRef.current.send(JSON.stringify(payload));
      setReplyToMessage(null);
      return true;
    }
    return false;
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(t('support.fileSizeExceeded', { 
        currentSize: (file.size / (1024 * 1024)).toFixed(2) 
      }));
      return;
    }

    setSelectedFile(file);
    setError('');
  };

  // Load chat details and messages from API (fast initial load)
  const loadChat = async () => {
    if (!chatId) return;
    
    try {
      const response = await api.getSupportChat(chatId);
      if (response.data && isMountedRef.current) {
        setChat(response.data);
        // Load messages from API immediately for fast display
        setMessages(response.data.messages || []);
        setLoading(false);
        // WebSocket will update messages in real-time when connected
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err.data?.errorText || t('support.failedToLoadChat'));
        setLoading(false);
        if (err?.status === 404) {
          onChatMissing?.();
        }
      }
    }
  };

  // Translate user message (admin only)
  const handleTranslateMessage = async (messageId) => {
    if (!isAdmin) return;
    
    try {
      setTranslatingMessageId(messageId);
      const response = await api.translateMessage(messageId, 'Russian');
      if (response.data) {
        // Update message in local state
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, translated_content: response.data.translated_content }
            : msg
        ));
      }
    } catch (err) {
      setError(err.data?.errorText || err.message || 'Ошибка перевода');
    } finally {
      setTranslatingMessageId(null);
    }
  };

  const handleMarkUnread = async (messageId) => {
    if (!isAdmin) return;
    try {
      setMarkingUnreadId(messageId);
      await api.markSupportMessageUnread(messageId);
      setMessages(prev =>
        prev.map(msg => (msg.id === messageId ? { ...msg, is_read: false } : msg))
      );
      onUnreadTotalsRefresh?.();
    } catch (err) {
      setError(err.data?.errorText || err.message || t('support.markUnreadFailed'));
    } finally {
      setMarkingUnreadId(null);
    }
  };

  // Load status choices for orders
  useEffect(() => {
    if (isAdmin) {
      const loadStatusChoices = async () => {
        try {
          const response = await api.request('/orders/status-choices/', { method: 'GET' });
          if (response.data) {
            setStatusChoices(response.data);
          }
        } catch (err) {
          console.error('Failed to load status choices:', err);
        }
      };
      loadStatusChoices();
    }
  }, [isAdmin]);

  // Load user orders for modal
  const loadUserOrders = useCallback(async () => {
    if (!chat?.user) return;
    
    setOrdersLoading(true);
    setOrdersError('');
    
    try {
      const params = {
        user_id: chat.user,
        page: ordersPage,
        page_size: 20,
      };
      
      if (ordersSearch) params.search = ordersSearch;
      if (ordersStatusFilter) params.status = ordersStatusFilter;
      
      const response = await api.getAdminOrders(params);
      if (response.data) {
        setOrders(response.data);
        setOrdersTotal(response.total || 0);
        setOrdersTotalPages(response.total_pages || 0);
      }
    } catch (err) {
      setOrdersError(err.data?.errorText || err.message || 'Ошибка загрузки заказов');
    } finally {
      setOrdersLoading(false);
    }
  }, [chat?.user, ordersPage, ordersSearch, ordersStatusFilter]);

  // Load orders when modal opens
  useEffect(() => {
    if (showOrdersModal && chat?.user) {
      loadUserOrders();
    }
  }, [showOrdersModal, loadUserOrders, chat?.user]);

  // Handle orders search
  const handleOrdersSearch = () => {
    setOrdersPage(1);
    loadUserOrders();
  };

  // Get status label
  const getStatusLabel = (statusValue) => {
    const statusChoice = statusChoices.find(s => s.value === statusValue);
    return statusChoice ? statusChoice.display : statusValue;
  };

  // Open Django Admin with user filter
  const openDjangoAdminOrders = () => {
    if (chat?.user) {
      window.open(`/admin/orders/order/?items__order__seller_id__in=${chat.user_info?.stores?.map(s => s.store_id).join(',') || ''}`, '_blank');
    }
  };

  // Show spinner only while loading
  if (loading) {
    return (
      <Card className="h-100">
        <Card.Body className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  if (!chat) {
    return (
      <Card className="h-100">
        <Card.Body>
          <Alert variant="danger">{t('support.chatNotFound')}</Alert>
        </Card.Body>
      </Card>
    );
  }

  const compactEmbeddedHeader = embeddedInChatApp && !isAdmin;

  return (
    <Card className={`h-100 d-flex flex-column ${embeddedInChatApp ? 'chat-app-support-chat' : ''}`} style={{ minHeight: 0 }}>
      {compactEmbeddedHeader ? (
        shouldShowSupportChatSubject(chat.subject) ? (
          <div className="chat-app-support-subject flex-shrink-0">
            {chat.subject}
          </div>
        ) : null
      ) : (
      <Card.Header className="flex-shrink-0">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            {showBackButton && onClose && (
              <Button variant="outline-secondary" size="sm" className="me-2" onClick={onClose}>
                <i className="bi bi-arrow-left"></i>
              </Button>
            )}
            <div>
              <h5 className="mb-0" style={{ color: 'var(--bs-body-color)', fontSize: showBackButton ? '1rem' : undefined }}>
                {isAdmin ? t('support.chatWith', { name: chat.user_name || chat.user_email }) : t('support.supportChat')}
                {shouldShowSupportChatSubject(chat.subject) && (
                  <small className="ms-2 d-none d-sm-inline" style={{ color: 'var(--bs-body-color)', fontWeight: 'normal' }}>
                    - {chat.subject}
                    {isAdmin && chat.subject_translated && chat.subject && chat.subject !== chat.subject_translated && (
                      <span className="ms-2" style={{ fontStyle: 'italic', opacity: 0.8 }}>
                        <i className="bi bi-translate me-1"></i>
                        {chat.subject_translated}
                      </span>
                    )}
                  </small>
                )}
              </h5>
              {shouldShowSupportChatSubject(chat.subject) && showBackButton && (
                <small className="d-block d-sm-none" style={{ color: 'var(--bs-body-color)' }}>
                  {chat.subject}
                  {isAdmin && chat.subject_translated && chat.subject && chat.subject !== chat.subject_translated && (
                    <span className="ms-2 d-block mt-1" style={{ fontStyle: 'italic', opacity: 0.8 }}>
                      <i className="bi bi-translate me-1"></i>
                      {chat.subject_translated}
                    </span>
                  )}
                </small>
              )}
              <div className="mt-1">
                {connected && (
                  <Badge bg="success">{t('support.online')}</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {isAdmin && chat.user && (
              <>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowOrdersModal(true)}
                  title="Заказы пользователя"
                >
                  <i className="bi bi-box-seam me-1"></i>
                  <span className="d-none d-lg-inline">Заказы</span>
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => window.open(`/admin/orders?user_id=${chat.user}`, '_blank')}
                  title="Открыть в админ-панели"
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i>
                  <span className="d-none d-lg-inline">Админ</span>
                </Button>
                <Button
                  variant={adminUserInfoVisible ? 'outline-info' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setAdminUserInfoVisible((v) => !v)}
                  title={adminUserInfoVisible ? 'Скрыть данные пользователя' : 'Показать данные пользователя'}
                >
                  <i className="bi bi-info-circle me-1"></i>
                  <span className="d-none d-lg-inline">Инфо</span>
                </Button>
              </>
            )}
            {onClose && !showBackButton && (
              <Button variant="outline-secondary" size="sm" onClick={onClose}>
                <i className="bi bi-x"></i>
              </Button>
            )}
          </div>
        </div>
        
        {/* User Info Panel */}
        {isAdmin && chat.user_info && adminUserInfoVisible && (
            <div className="mt-3 p-3 rounded support-user-info-panel" style={{ 
              backgroundColor: 'var(--bg-tertiary)', 
              color: 'var(--text-primary)'
            }}>
              <Row>
                <Col xs={12} md={6}>
                  <h6 className="mb-2 support-user-info-text" style={{ color: 'var(--text-primary) !important' }}>
                    <i className="bi bi-person-circle me-2"></i>Пользователь
                  </h6>
                  <div className="small support-user-info-text">
                    <p className="mb-1 support-user-info-text"><strong className="support-user-info-text">Email:</strong> <span className="support-user-info-text">{chat.user_info.email}</span></p>
                    <p className="mb-1 support-user-info-text"><strong className="support-user-info-text">Имя:</strong> <span className="support-user-info-text">{chat.user_info.full_name}</span></p>
                    {chat.user_info.delivery_address_phone && (
                      <p className="mb-1 support-user-info-text"><strong className="support-user-info-text">Телефон:</strong> <span className="support-user-info-text">{chat.user_info.delivery_address_phone}</span></p>
                    )}
                    {(chat.user_info.delivery_address_city || chat.user_info.delivery_address_country) && (
                      <p className="mb-1 support-user-info-text">
                        <strong className="support-user-info-text">Локация:</strong> <span className="support-user-info-text">{[chat.user_info.delivery_address_city, chat.user_info.delivery_address_country].filter(Boolean).join(', ')}</span>
                      </p>
                    )}
                    {chat.user_info.delivery_address_street && (
                      <p className="mb-1 support-user-info-text">
                        <strong className="support-user-info-text">Адрес:</strong>{' '}
                        <span className="support-user-info-text">{chat.user_info.delivery_address_street}</span>
                        {chat.user_info.delivery_address_postal_code ? (
                          <span className="support-user-info-text">, {chat.user_info.delivery_address_postal_code}</span>
                        ) : null}
                      </p>
                    )}
                    {chat.user_info.wechat_id && (
                      <p className="mb-1 support-user-info-text">
                        <strong className="support-user-info-text">WeChat:</strong>{' '}
                        <span className="support-user-info-text">{chat.user_info.wechat_id}</span>
                      </p>
                    )}
                    {chat.user_info.telegram_username && (
                      <p className="mb-1 support-user-info-text">
                        <strong className="support-user-info-text">Telegram:</strong>{' '}
                        <span className="support-user-info-text">
                          {String(chat.user_info.telegram_username).startsWith('@')
                            ? chat.user_info.telegram_username
                            : `@${chat.user_info.telegram_username}`}
                        </span>
                      </p>
                    )}
                    {chat.user_info.balance_top_up != null && chat.user_info.balance_top_up !== '' && (
                      <p className="mb-1 support-user-info-text">
                        <strong className="support-user-info-text">Пополнение баланса (учёт):</strong>{' '}
                        <span className="support-user-info-text">{chat.user_info.balance_top_up} ¥</span>
                      </p>
                    )}
                    <p className="mb-1 support-user-info-text">
                      <strong className="support-user-info-text">Статус:</strong>{' '}
                      {chat.user_info.is_active ? (
                        <Badge bg="success" className="ms-1">Активен</Badge>
                      ) : (
                        <Badge bg="danger" className="ms-1">Неактивен</Badge>
                      )}
                      {chat.user_info.is_email_verified && (
                        <Badge bg="info" className="ms-1">Email подтверждён</Badge>
                      )}
                    </p>
                    <p className="mb-0 support-user-info-text"><strong className="support-user-info-text">Регистрация:</strong> <span className="support-user-info-text">{new Date(chat.user_info.date_joined).toLocaleDateString()}</span></p>
                  </div>
                </Col>
                <Col xs={12} md={6} className="mt-3 mt-md-0">
                  <h6 className="mb-2 support-user-info-text" style={{ color: 'var(--text-primary) !important' }}>
                    <i className="bi bi-shop me-2"></i>
                    Магазины ({chat.user_info.verified_stores_count}/{chat.user_info.stores_count})
                  </h6>
                  {chat.user_info.stores && chat.user_info.stores.length > 0 ? (
                    <div className="small" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {chat.user_info.stores.map((store, idx) => (
                        <div key={store.id} className="mb-2 p-2 rounded support-user-info-text" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="support-user-info-text" style={{ color: 'var(--text-primary) !important' }}>
                              <strong className="support-user-info-text">{store.store_type_display}</strong>
                              {store.name && <span className="ms-1 support-user-info-text">({store.name})</span>}
                            </span>
                            <Badge bg={store.verification_status === 'verified' ? 'success' : store.verification_status === 'pending' ? 'warning' : 'danger'}>
                              {store.verification_status_display}
                            </Badge>
                          </div>
                          <small className="support-user-info-text" style={{ color: 'var(--text-primary) !important' }}>ID: {store.store_id}</small>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="small mb-0 support-user-info-text" style={{ color: 'var(--text-primary) !important' }}>Нет магазинов</p>
                  )}
                </Col>
              </Row>
            </div>
        )}
      </Card.Header>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="m-2 mb-0">
          {error}
        </Alert>
      )}

      <Card.Body
        ref={messagesContainerRef}
        className="flex-grow-1 overflow-auto"
        style={{ minHeight: 0 }}
        onScroll={handleMessagesScroll}
      >
        <SupportMessageList
          messages={messages}
          userId={user?.id}
          isAdmin={isAdmin}
          embeddedInChatApp={embeddedInChatApp}
          language={language}
          t={t}
          highlightedMessageId={highlightedMessageId}
          translatingMessageId={translatingMessageId}
          markingUnreadId={markingUnreadId}
          setMessageRef={setMessageRef}
          onReply={handleReplyToMessage}
          onJumpToReply={jumpToMessage}
          onTranslateMessage={handleTranslateMessage}
          onMarkUnread={handleMarkUnread}
          messagesEndRef={messagesEndRef}
        />
      </Card.Body>

      <Card.Footer className="flex-shrink-0">
          <>
            {selectedFile && (
              <div className="mb-2 p-2 bg-light rounded d-flex justify-content-between align-items-center">
                <div>
                  <i className="bi bi-paperclip me-2"></i>
                  <span>{selectedFile.name}</span>
                  <small className="text-muted ms-2">
                    ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                  </small>
                </div>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  <i className="bi bi-x"></i>
                </Button>
              </div>
            )}
            {isAdmin && (
              <div
                className="mb-3 p-2 rounded border"
                style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
              >
                <div className="small fw-bold mb-2">{t('support.aiSuggestTitle')}</div>
                <Form.Control
                  as="textarea"
                  rows={2}
                  className="mb-2"
                  placeholder={t('support.enterMessage')}
                  value={aiQuestionDraft}
                  onChange={(e) => setAiQuestionDraft(e.target.value)}
                />
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={async () => {
                      setAiSuggestError('');
                      try {
                        const text = await navigator.clipboard.readText();
                        setAiQuestionDraft(text);
                      } catch {
                        setAiSuggestError(t('support.aiSuggestError'));
                      }
                    }}
                  >
                    {t('support.aiSuggestPasteClipboard')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    disabled={aiSuggestLoading || !aiQuestionDraft.trim()}
                    onClick={async () => {
                      setAiSuggestError('');
                      setAiSuggestedReply('');
                      setAiSuggestLoading(true);
                      try {
                        const res = await api.postSupportAdminAiReplySuggest(aiQuestionDraft);
                        const s = res?.data?.suggested_reply;
                        if (s) setAiSuggestedReply(s);
                        else setAiSuggestError(t('support.aiSuggestError'));
                      } catch (err) {
                        setAiSuggestError(err.data?.errorText || t('support.aiSuggestError'));
                      } finally {
                        setAiSuggestLoading(false);
                      }
                    }}
                  >
                    {aiSuggestLoading ? t('support.aiSuggestLoading') : t('support.aiSuggestGetDraft')}
                  </Button>
                </div>
                {aiSuggestError && (
                  <Alert variant="warning" className="py-1 small mb-2">
                    {aiSuggestError}
                  </Alert>
                )}
                {aiSuggestedReply && (
                  <div>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      readOnly
                      className="mb-2 small"
                      value={aiSuggestedReply}
                    />
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => setComposerInsert({ text: aiSuggestedReply, nonce: Date.now() })}
                    >
                      {t('support.aiSuggestInsert')}
                    </Button>
                  </div>
                )}
              </div>
            )}
            <SupportMessageComposer
              isAdmin={isAdmin}
              connected={connected}
              uploadingFile={uploadingFile}
              selectedFile={selectedFile}
              replyToMessage={replyToMessage}
              onClearReply={() => setReplyToMessage(null)}
              onSend={handleComposerSend}
              onPickFile={() => fileInputRef.current?.click()}
              insertRequest={composerInsert}
            />
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </>
      </Card.Footer>

      {/* Orders Modal */}
      <Modal 
        show={showOrdersModal} 
        onHide={() => setShowOrdersModal(false)} 
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: 'var(--bs-body-color)' }}>
            <i className="bi bi-box-seam me-2"></i>
            Заказы пользователя
            {chat?.user_info && (
              <small className="ms-2" style={{ color: 'var(--bs-secondary-color)' }}>
                ({chat.user_info.full_name})
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {ordersError && (
            <Alert variant="danger" dismissible onClose={() => setOrdersError('')}>
              {ordersError}
            </Alert>
          )}

          {/* Filters */}
          <Row className="mb-3 g-2">
            <Col xs={12} sm={6} md={4}>
              <InputGroup size="sm">
                <Form.Control
                  type="text"
                  placeholder="Поиск по номеру, SKU..."
                  value={ordersSearch}
                  onChange={(e) => setOrdersSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleOrdersSearch()}
                />
                <Button variant="outline-secondary" onClick={handleOrdersSearch}>
                  <i className="bi bi-search"></i>
                </Button>
              </InputGroup>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Form.Select 
                size="sm" 
                value={ordersStatusFilter} 
                onChange={(e) => { setOrdersStatusFilter(e.target.value); setOrdersPage(1); }}
              >
                <option value="">Все статусы</option>
                {statusChoices.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.display}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={5} className="text-md-end">
              <small style={{ color: 'var(--bs-secondary-color)' }}>
                Найдено заказов: {ordersTotal}
              </small>
            </Col>
          </Row>

          {/* Orders Table */}
          {ordersLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-5" style={{ color: 'var(--bs-secondary-color)' }}>
              <i className="bi bi-inbox display-4 d-block mb-2"></i>
              Заказы не найдены
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <Table hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ color: 'var(--bs-body-color)' }}>№ заказа</th>
                      <th style={{ color: 'var(--bs-body-color)' }}>Статус</th>
                      <th style={{ color: 'var(--bs-body-color)' }}>Магазин</th>
                      <th className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>Товар</th>
                      <th className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>Дата</th>
                      <th style={{ color: 'var(--bs-body-color)' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const item = order.items?.[0];
                      const isExpanded = expandedOrderRow === order.id;
                      return (
                        <React.Fragment key={order.id}>
                          <tr>
                            <td style={{ color: 'var(--bs-body-color)' }}>
                              <small>{order.platform_order_no}</small>
                            </td>
                            <td>
                              <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                                {getStatusLabel(order.status)}
                              </Badge>
                            </td>
                            <td style={{ color: 'var(--bs-body-color)' }}>
                              <small>{order.seller_id || '-'}</small>
                            </td>
                            <td className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>
                              {item && (
                                <small>
                                  {item.sku_name ? (
                                    item.sku_name.length > 30 ? item.sku_name.substring(0, 30) + '...' : item.sku_name
                                  ) : item.sku_code}
                                </small>
                              )}
                            </td>
                            <td className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>
                              <small>{new Date(order.created_at).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={() => setExpandedOrderRow(isExpanded ? null : order.id)}
                              >
                                <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                              </Button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="6" style={{ backgroundColor: 'var(--bs-tertiary-bg)' }}>
                                <div className="p-2">
                                  <Row>
                                    <Col xs={12} md={6}>
                                      <small style={{ color: 'var(--bs-body-color)' }}>
                                        {item && (
                                          <>
                                            <p className="mb-1"><strong>Товар:</strong> {item.sku_name || item.sku_code}</p>
                                            <p className="mb-1"><strong>SKU:</strong> {item.sku_code}</p>
                                            {item.sku_barcode && <p className="mb-1"><strong>Штрихкод:</strong> {item.sku_barcode}</p>}
                                            <p className="mb-1 text-muted small">
                                              <strong>Габариты / вес:</strong>{' '}
                                              {(() => {
                                                const L = item.sku_length;
                                                const W = item.sku_width;
                                                const H = item.sku_height;
                                                const hasDims =
                                                  (L != null && Number(L) > 0) ||
                                                  (W != null && Number(W) > 0) ||
                                                  (H != null && Number(H) > 0);
                                                const wkg =
                                                  item.sku_weight_kg != null && item.sku_weight_kg !== ''
                                                    ? Number(item.sku_weight_kg)
                                                    : NaN;
                                                const hasW = !Number.isNaN(wkg) && wkg > 0;
                                                if (!hasDims && !hasW) return 'не указаны';
                                                const d = hasDims ? `${L || 0}×${W || 0}×${H || 0} см` : '—';
                                                const w = hasW ? `${wkg} кг` : 'вес неизвестен';
                                                return `${d} · ${w}`;
                                              })()}
                                            </p>
                                            <p className="mb-1"><strong>Брак:</strong> {item.is_defective !== null ? (item.is_defective ? 'Да' : 'Нет') : '-'}</p>
                                          </>
                                        )}
                                      </small>
                                    </Col>
                                    <Col xs={12} md={6}>
                                      <small style={{ color: 'var(--bs-body-color)' }}>
                                        {order.tracking_number && <p className="mb-1"><strong>Трек:</strong> {order.tracking_number}</p>}
                                        {order.customer_tracking_number && <p className="mb-1"><strong>Трек клиента:</strong> {order.customer_tracking_number}</p>}
                                        {order.seller_tracking_number && <p className="mb-1"><strong>Трек продавца:</strong> {order.seller_tracking_number}</p>}
                                        <p className="mb-1"><strong>Создан:</strong> {new Date(order.created_at).toLocaleString()}</p>
                                      </small>
                                      {item?.attachments?.length > 0 && (
                                        <div className="mt-2">
                                          <strong className="small">Вложения:</strong>
                                          <div className="d-flex flex-wrap gap-1 mt-1">
                                            {item.attachments.map((att) => (
                                              <img
                                                key={att.id}
                                                src={api.getAuthenticatedMediaUrl(att.file_url)}
                                                alt={att.file_name}
                                                style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                                                onClick={() => window.open(api.getAuthenticatedMediaUrl(att.file_url), '_blank')}
                                              />
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </Col>
                                  </Row>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {ordersTotalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination size="sm">
                    <Pagination.First onClick={() => setOrdersPage(1)} disabled={ordersPage === 1} />
                    <Pagination.Prev onClick={() => setOrdersPage(p => Math.max(1, p - 1))} disabled={ordersPage === 1} />
                    {[...Array(Math.min(5, ordersTotalPages))].map((_, i) => {
                      let page;
                      if (ordersTotalPages <= 5) {
                        page = i + 1;
                      } else if (ordersPage <= 3) {
                        page = i + 1;
                      } else if (ordersPage >= ordersTotalPages - 2) {
                        page = ordersTotalPages - 4 + i;
                      } else {
                        page = ordersPage - 2 + i;
                      }
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === ordersPage}
                          onClick={() => setOrdersPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next onClick={() => setOrdersPage(p => Math.min(ordersTotalPages, p + 1))} disabled={ordersPage === ordersTotalPages} />
                    <Pagination.Last onClick={() => setOrdersPage(ordersTotalPages)} disabled={ordersPage === ordersTotalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => window.open(`/admin/orders?user_id=${chat?.user}`, '_blank')}
          >
            <i className="bi bi-box-arrow-up-right me-1"></i>
            Открыть в полной версии
          </Button>
          <Button variant="secondary" onClick={() => setShowOrdersModal(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default SupportChat;

